"use client"

import { useMemo, useState } from "react"
import { format } from "date-fns"
import { useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import { asArray } from "@/lib/as-array"
import { getCommentLabel } from "@/lib/comment-labels"
import {
  useDeleteCommentsCommentId,
  useGetComments,
  usePostComments,
  usePutCommentsCommentId,
} from "openapi/api/endpoints"

/**
 * The notes on a job: reading them, searching them, and the three writes.
 *
 * Split out of the screen so canvas 18a's job is only to draw rows — the old
 * component fetched, mutated, formatted and rendered in one 370-line file.
 */

export type JobNote = {
  id: string
  /** Rich text, as stored. */
  body: string
  /** The same text with the markup stripped, for searching and length checks. */
  plain: string
  label: string
  author: string
  when: string
}

const NEW_NOTE_LABEL = "client_interview_notes"

const stripMarkup = (html: string) => html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim()

function formatWhen(iso?: string) {
  if (!iso) return "—"
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return "—"
  // Day first, as every other mono date in Graphite writes it — "28 Jul", not
  // whatever order the viewer's locale happens to prefer.
  const sameYear = date.getFullYear() === new Date().getFullYear()
  return format(date, sameYear ? "d MMM" : "d MMM yyyy")
}

export function useJobNotes(jobId: string) {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState("")

  const { data, isLoading, isError } = useGetComments({
    entity_type: "jobs",
    entity_id: jobId,
  })

  const { mutateAsync: createComment, isPending: isCreating } = usePostComments()
  const { mutateAsync: updateComment, isPending: isUpdating } = usePutCommentsCommentId()
  const { mutateAsync: removeComment } = useDeleteCommentsCommentId()

  /*
    `?? []` only guards null. This endpoint reaching `.map` as an object threw
    and took the whole Notes tab down.
  */
  const notes = useMemo<JobNote[]>(() => {
    return asArray<Record<string, unknown>>(data)
      .filter((comment) => Boolean(comment.id))
      .map((comment) => {
        const body = (comment.body as string) ?? ""
        return {
          id: comment.id as string,
          body,
          plain: stripMarkup(body),
          label: getCommentLabel((comment.label as string) ?? "note"),
          author: (comment.username as string) ?? "Unknown",
          when: formatWhen(comment.created_at as string | undefined),
        }
      })
  }, [data])

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase()
    if (!needle) return notes
    return notes.filter(
      (note) =>
        note.plain.toLowerCase().includes(needle) ||
        note.author.toLowerCase().includes(needle) ||
        note.label.toLowerCase().includes(needle),
    )
  }, [notes, search])

  // Every write ends the same way, so the refresh lives in one place.
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["/comments"], exact: false })
  }

  const create = async (body: string) => {
    if (!body.trim()) return false
    try {
      await createComment({
        data: { label: NEW_NOTE_LABEL, body, entity_type: "jobs", entity_id: jobId },
      })
      await refresh()
      return true
    } catch (error) {
      toast.error("Could not save the note", { description: `${error}` })
      return false
    }
  }

  const update = async (noteId: string, body: string) => {
    if (!body.trim()) return false
    try {
      await updateComment({ commentId: noteId, data: { label: NEW_NOTE_LABEL, body } })
      await refresh()
      return true
    } catch (error) {
      toast.error("Could not update the note", { description: `${error}` })
      return false
    }
  }

  const remove = async (noteId: string) => {
    try {
      await removeComment({ commentId: noteId })
      await refresh()
      return true
    } catch (error) {
      toast.error("Could not delete the note", { description: `${error}` })
      return false
    }
  }

  return {
    notes: visible,
    total: notes.length,
    /** True only when the job genuinely has none, not while a search misses. */
    isEmpty: notes.length === 0,
    search,
    setSearch,
    isLoading,
    isError,
    isSaving: isCreating || isUpdating,
    create,
    update,
    remove,
  }
}
